// src/components/Sidebar.tsx - UPDATED WITH MANIFESTO
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home,
  BarChart3,
  MapPin,
  MessageSquare,
  FileText,
  Bell,
  Settings,
  Users,
  Smartphone,
  Shield,
  LogOut,
  Menu,
  X,
  User,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  BookOpen, // Add this import for Manifesto icon
} from 'lucide-react';

interface SidebarProps {
  userRole?: string; // Optional prop to override the role from auth context
}

const AdminNavItems = [
  { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/reports', icon: BarChart3, label: 'Reports' },
  { path: '/admin/campaigns', icon: MessageSquare, label: 'Campaigns' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
  { path: '/admin/aspirants', icon: Users, label: 'Aspirants' },
];

const AspirantNavItems = [
  { path: '/aspirant/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/aspirant/memorandums', icon: FileText, label: 'Memorandums' },
  { path: '/aspirant/campaigns', icon: MessageSquare, label: 'SMS Campaigns' },
  { path: '/aspirant/reports', icon: BarChart3, label: 'Ward Reports' },
  { path: '/aspirant/heatmap', icon: MapPin, label: 'Heat Map' },
  { path: '/aspirant/manifesto', icon: BookOpen, label: 'Manifesto' }, // ADDED THIS LINE
  { path: '/aspirant/settings', icon: Settings, label: 'Settings' },
];

const CitizenNavItems = [
  { path: '/citizen/dashboard', icon: Home, label: 'Dashboard' },
  { path: '/citizen/report', icon: AlertCircle, label: 'Report Issue' },
  { path: '/citizen/profile', icon: User, label: 'Profile' },
];

export const Sidebar: React.FC<SidebarProps> = ({ userRole }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { user, aspirant, logout } = useAuth();
  const location = useLocation();

  // Use prop or context for role
  const currentRole = userRole || user?.role;

  // Auto-collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    // Set initial state
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getNavItems = () => {
    if (!currentRole) return [];
    switch (currentRole) {
      case 'ADMIN':
      case 'STAFF':
        return AdminNavItems;
      case 'ASPIRANT':
        return AspirantNavItems;
      case 'CITIZEN':
        return CitizenNavItems;
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  const toggleSidebar = () => {
    if (window.innerWidth < 1024) {
      setIsMobileOpen(!isMobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.fullName) return user.fullName;
    if (user?.username) return user.username;
    if (aspirant?.name) return aspirant.name;
    return 'User';
  };

  // Get user display initial
  const getUserInitial = () => {
    if (user?.fullName) return user.fullName.charAt(0).toUpperCase();
    if (user?.username) return user.username.charAt(0).toUpperCase();
    if (aspirant?.name) return aspirant.name.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar Container */}
      <div className={`
        fixed inset-y-0 left-0 z-40 
        transition-all duration-300 ease-in-out
        ${isCollapsed ? 'w-0 lg:w-20' : 'w-64'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Sidebar Content */}
        <div className={`
          h-full bg-white shadow-lg overflow-hidden
          ${isCollapsed ? 'w-20' : 'w-64'}
          flex flex-col
        `}>
          
          {/* Header with Toggle */}
          <div className="flex items-center justify-between h-16 px-4 border-b">
            {!isCollapsed && (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">CC</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Citizen Connect</h1>
                  <p className="text-xs text-gray-500 capitalize">
                    {currentRole?.toLowerCase() || 'user'} panel
                  </p>
                </div>
              </div>
            )}
            
            {isCollapsed && (
              <div className="mx-auto">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">CC</span>
                </div>
              </div>
            )}

            {/* Toggle Button - Desktop */}
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex p-2 rounded-md hover:bg-gray-100"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>

            {/* Close Button - Mobile */}
            <button
              onClick={() => setIsMobileOpen(false)}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              aria-label="Close sidebar"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 mt-6 px-4 overflow-y-auto">
            <div className="space-y-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileOpen(false)}
                  className={({ isActive }) => `
                    flex items-center rounded-lg transition-colors
                    ${isCollapsed ? 'justify-center px-3 py-3' : 'space-x-3 px-4 py-3'}
                    ${isActive
                      ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                  title={isCollapsed ? item.label : ''}
                  aria-label={item.label}
                >
                  <item.icon size={isCollapsed ? 22 : 20} />
                  {!isCollapsed && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>

          {/* User Info */}
          <div className="p-4 border-t">
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
              <div className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`}>
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-700 font-semibold">
                    {getUserInitial()}
                  </span>
                </div>
                
                {!isCollapsed && (
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                      {getUserDisplayName()}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      {currentRole?.toLowerCase() || 'user'}
                    </p>
                  </div>
                )}
              </div>
              
              {!isCollapsed && (
                <button
                  onClick={logout}
                  className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                  title="Logout"
                  aria-label="Logout"
                >
                  <LogOut size={20} />
                </button>
              )}

              {isCollapsed && (
                <div className="mt-2">
                  <button
                    onClick={logout}
                    className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                    title="Logout"
                    aria-label="Logout"
                  >
                    <LogOut size={20} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area Adjustment */}
      <div className={`
        transition-all duration-300 ease-in-out
        ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}
        ${isMobileOpen ? 'ml-64' : 'ml-0'}
        min-h-screen
      `}>
        {/* Mobile Menu Toggle Button (only shows when sidebar is hidden) */}
        {!isMobileOpen && (
          <div className="lg:hidden fixed top-4 left-4 z-30">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md bg-white shadow-md hover:bg-gray-50"
              aria-label="Open sidebar"
            >
              <Menu size={24} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};